/**
 * PurchasesModals — Vendor / Payment / Create PO / Edit Bill modals.
 *
 * Extracted from Purchases.jsx (iter200) to keep the main page under 1700
 * lines. Each modal is a small controlled view — write logic + state still
 * lives in the parent component.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, CreditCard, RefreshCw } from 'lucide-react';
import VendorCombobox from '../VendorCombobox';
import ExtraChargesEditor from '../ExtraChargesEditor';
import BillPaymentHistory from './BillPaymentHistory';
import RawMaterialNameAutocomplete from '../RawMaterialNameAutocomplete';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// ─────────────────────────────────────────────────────────────
// VendorModal — add-vendor form
// ─────────────────────────────────────────────────────────────
const VENDOR_FIELDS = [
  ['name', 'Vendor Name *'],
  ['contact_person', 'Contact Person'],
  ['phone', 'Phone'],
  ['email', 'Email'],
  ['gst_number', 'GST Number'],
  ['city', 'City'],
  ['state', 'State'],
  ['product_categories', 'Categories (comma-separated)'],
];

export function VendorModal({ open, vendorForm, setVendorForm, submitting, onClose, onSubmit }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 className="font-heading font-semibold text-zinc-50">Add Vendor</h3>
          <button onClick={onClose}><X size={18} className="text-zinc-500" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-3">
          {VENDOR_FIELDS.map(([field, label]) => (
            <div key={field}>
              <label className="text-zinc-400 text-xs mb-1 block">{label}</label>
              <input required={field === 'name'} value={vendorForm[field]} onChange={e => setVendorForm(f => ({ ...f, [field]: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          ))}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
            <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Saving...' : 'Add Vendor'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PaymentModal — record a payment against a bill
// ─────────────────────────────────────────────────────────────
export function PaymentModal({
  paymentModal,
  setPaymentModal,
  paymentForm,
  setPaymentForm,
  submittingPayment,
  setBills,
  onClose,
  onSubmit,
}) {
  if (!paymentModal) return null;
  const outstanding = parseFloat(paymentModal.total_amount || 0) - parseFloat(paymentModal.paid_amount || 0);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <h3 className="font-heading font-semibold text-zinc-50">Record Payment</h3>
            <p className="text-zinc-500 text-xs mt-0.5">{paymentModal.vendor_name} · {fmt(paymentModal.total_amount)}</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="payment-form">
          <div className="bg-zinc-800 rounded-xl p-3 text-xs space-y-1">
            <div className="flex justify-between text-zinc-400"><span>Bill Total</span><span className="font-mono">{fmt(paymentModal.total_amount)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>Previously Paid</span><span className="font-mono text-green-400">{fmt(paymentModal.paid_amount || 0)}</span></div>
            <div className="flex justify-between text-amber-400 font-semibold border-t border-zinc-700 pt-1"><span>Outstanding</span><span className="font-mono">{fmt(outstanding)}</span></div>
          </div>

          {/* iter181 — Payment history with edit/delete */}
          <BillPaymentHistory
            billId={paymentModal.id}
            onChanged={async () => {
              const r = await axios.get(`${API}/purchases/bills`, { withCredentials: true });
              const fresh = (r.data || []).find(b => b.id === paymentModal.id);
              if (fresh) {
                setPaymentModal(fresh);
                setBills(prev => prev.map(b => b.id === fresh.id ? fresh : b));
              }
            }}
          />

          <div>
            <label className="text-zinc-400 text-xs mb-1.5 block">Amount Paid (₹) *</label>
            <input
              required type="number" step="any" min="0"
              value={paymentForm.paid_amount}
              onChange={e => setPaymentForm(f => ({ ...f, paid_amount: e.target.value }))}
              data-testid="payment-amount-input"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-amber-400 font-mono text-sm font-semibold focus:outline-none focus:border-amber-500"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1.5 block">Payment Date</label>
            <input
              type="date" value={paymentForm.payment_date}
              onChange={e => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs mb-1.5 block">Payment Method</label>
            <select value={paymentForm.payment_method} onChange={e => setPaymentForm(f => ({ ...f, payment_method: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
              {['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque', 'Cash', 'DD'].map(m => (
                <option key={m} value={m} className="bg-zinc-900">{m}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm">Cancel</button>
            <button type="submit" disabled={submittingPayment} data-testid="confirm-payment-btn"
              className="flex-1 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {submittingPayment ? <RefreshCw size={14} className="animate-spin" /> : <CreditCard size={14} />}
              {submittingPayment ? 'Saving...' : 'Mark as Paid'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CreatePOModal — quick-PO create with mini line-items
// ─────────────────────────────────────────────────────────────
export function CreatePOModal({
  open,
  poForm,
  setPoForm,
  vendors,
  materials,
  isAdmin,
  lookupItemCode,
  addPOItem,
  updatePOItem,
  removePOItem,
  poSubtotal,
  poTotal,
  submitting,
  onClose,
  onSubmit,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 className="font-heading font-semibold text-zinc-50">Create Purchase Order</h3>
          <button onClick={onClose}><X size={18} className="text-zinc-500" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          <div>
            <label className="text-zinc-400 text-xs mb-1.5 block">Vendor *</label>
            <select required value={poForm.vendor_id} onChange={e => setPoForm(f => ({ ...f, vendor_id: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
              <option value="">Select vendor...</option>
              {vendors.map(v => <option key={v.id} value={v.id} className="bg-zinc-900">{v.name}</option>)}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-zinc-400 text-xs">Items *</label>
              <button type="button" onClick={addPOItem} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"><Plus size={12} /> Add Item</button>
            </div>
            <div className="space-y-2">
              {poForm.items.map((item, i) => (
                <div key={`po-item-${i}-${item.material_id || 'new'}`} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5">
                    <select value={item.material_id} onChange={e => updatePOItem(i, 'material_id', e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-2 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500">
                      <option value="">Material...</option>
                      {materials.map(m => {
                        const code = isAdmin ? lookupItemCode(m.name) : '';
                        return <option key={m.id} value={m.id} className="bg-zinc-900">{code ? `${code} · ${m.name}` : m.name}</option>;
                      })}
                    </select>
                  </div>
                  <div className="col-span-3">
                    <input type="number" placeholder="Qty" value={item.quantity} onChange={e => updatePOItem(i, 'quantity', e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-2 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500" />
                  </div>
                  <div className="col-span-3">
                    <input type="number" placeholder="Price/unit" value={item.unit_price} onChange={e => updatePOItem(i, 'unit_price', e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-2 py-2 text-zinc-50 text-xs focus:outline-none focus:border-amber-500" />
                  </div>
                  <button type="button" onClick={() => removePOItem(i)} className="col-span-1 text-zinc-600 hover:text-red-400"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">GST Rate (%)</label>
              <select value={poForm.gst_rate} onChange={e => setPoForm(f => ({ ...f, gst_rate: parseInt(e.target.value) }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                {[0, 5, 12, 18, 28].map(r => <option key={r} value={r} className="bg-zinc-900">{r}%</option>)}
              </select>
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">Expected Delivery</label>
              <input type="date" onChange={e => setPoForm(f => ({ ...f, expected_delivery: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          </div>
          {poSubtotal > 0 && (
            <div className="bg-zinc-800 rounded-xl p-3 text-xs space-y-1">
              <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span className="font-mono">{fmt(poSubtotal)}</span></div>
              <div className="flex justify-between text-zinc-400"><span>GST ({poForm.gst_rate}%)</span><span className="font-mono">{fmt(poSubtotal * poForm.gst_rate / 100)}</span></div>
              <div className="flex justify-between text-amber-400 font-semibold border-t border-zinc-700 pt-1"><span>Total</span><span className="font-mono">{fmt(poTotal)}</span></div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
            <button type="submit" disabled={submitting} className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50">{submitting ? 'Creating...' : 'Create PO'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// EditBillModal — Sprint B (iter164): full bill edit with reverse-recreate
// ─────────────────────────────────────────────────────────────
export function EditBillModal({ editingBill, setEditingBill, editBillSaving, setEditBillSaving, setBills, materials = [], onClose }) {
  if (!editingBill) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setEditBillSaving(true);
    try {
      const payload = {
        vendor_name: editingBill.vendor_name,
        vendor_id: editingBill.vendor_id || '',
        invoice_number: editingBill.invoice_number,
        invoice_date: editingBill.invoice_date,
        due_date: (editingBill.due_date || '').slice(0, 10),
        notes: editingBill.notes || '',
        extra_charges: (editingBill.extra_charges || [])
          .filter(c => parseFloat(c.amount) > 0)
          .map(c => ({ label: c.label || 'Other', amount: parseFloat(c.amount) || 0 })),
        items: editingBill.items.map(it => ({
          product_name: it.product_name,
          quantity: parseFloat(it.quantity) || 0,
          container_type: it.container_type || 'kg',
          unit_price: parseFloat(it.unit_price) || 0,
          subtotal: parseFloat(it.subtotal) || 0,
          gst_rate: parseFloat(it.gst_rate) || 0,
          gst_amount: parseFloat(it.gst_amount) || 0,
          total_amount: parseFloat(it.total_amount) || 0,
          hsn_code: it.hsn_code || '',
          lot_no: it.lot_no || '',
          lot_id: it.lot_id || '',
          receiving_date: it.receiving_date || editingBill.invoice_date,
        })),
      };
      await axios.put(`${API}/purchases/bills/${editingBill.id}`, payload, { withCredentials: true });
      const r = await axios.get(`${API}/purchases/bills`, { withCredentials: true });
      setBills(r.data || []);
      onClose();
      toast.success('Bill updated. Inventory + cashflow refreshed.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Edit failed');
    } finally {
      setEditBillSaving(false);
    }
  };

  const itemsTotal = editingBill.items.reduce((s, x) => s + (parseFloat(x.total_amount) || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-[60] p-3 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-900 border border-amber-700/40 rounded-2xl w-full max-w-5xl my-4 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="edit-bill-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
          <div>
            <h3 className="font-heading font-semibold text-zinc-50">Edit Purchase Bill</h3>
            <p className="text-zinc-500 text-[10px] mt-0.5">Saving will REVERSE current lots/inventory/cashflow + voucher and recreate them from the new values.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={18}/></button>
        </div>
        <form className="overflow-y-auto p-5 space-y-3" onSubmit={onSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-zinc-400 text-xs mb-1 block">Vendor</label>
              <VendorCombobox
                testid="edit-bill-vendor"
                value={editingBill.vendor_name}
                onChange={v => setEditingBill(b => ({ ...b, vendor_name: v }))}
                onSelect={v => setEditingBill(b => ({ ...b, vendor_name: v.name, vendor_id: v.id, gstin: v.gstin || b.gstin }))}
              />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block flex items-center gap-1.5">
                Invoice No.
                <span className="text-amber-400 text-[9px] uppercase tracking-wider font-semibold">cascades</span>
              </label>
              <input value={editingBill.invoice_number || ''}
                onChange={e => setEditingBill(b => ({ ...b, invoice_number: e.target.value }))}
                data-testid="edit-bill-invoice-no"
                title="Renaming propagates to the linked voucher, payment vouchers, RM lots, and every cashflow row referencing this bill."
                className="w-full bg-zinc-800 border border-amber-700/40 rounded-lg px-3 py-2 text-amber-100 text-sm font-mono focus:outline-none focus:border-amber-500"/>
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Invoice Date</label>
              <input type="date" value={(editingBill.invoice_date || '').slice(0,10)}
                onChange={e => {
                  const newInv = e.target.value;
                  setEditingBill(b => {
                    // iter209 — Auto-recompute due_date when invoice_date changes
                    // so vendor reminders don't keep flagging the bill against the
                    // OLD invoice date. Operator can still override via Due Date field.
                    const credit = parseInt(b.credit_period_days || 30, 10) || 30;
                    let newDue = b.due_date || '';
                    if (newInv) {
                      try {
                        const d = new Date(newInv + 'T00:00:00');
                        d.setDate(d.getDate() + credit);
                        newDue = d.toISOString().slice(0, 10);
                      } catch { /* keep prior */ }
                    }
                    return { ...b, invoice_date: newInv, due_date: newDue };
                  });
                }}
                data-testid="edit-bill-invoice-date"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"/>
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Due Date</label>
              <input type="date" value={(editingBill.due_date || '').slice(0,10)}
                onChange={e => setEditingBill(b => ({ ...b, due_date: e.target.value }))}
                data-testid="edit-bill-due-date"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500"/>
              <p className="text-[10px] text-zinc-500 mt-1">Auto-set from Invoice Date + {editingBill.credit_period_days || 30}d credit period. Override here if needed.</p>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-zinc-300 text-xs font-semibold">Items ({editingBill.items.length})</div>
              <button type="button"
                data-testid="edit-bill-add-line"
                onClick={() => setEditingBill(b => ({ ...b, items: [...b.items, { product_name: '', quantity: '', container_type: 'kg', unit_price: '', subtotal: '', gst_rate: 5, gst_amount: 0, total_amount: 0, hsn_code: '', lot_no: '', receiving_date: editingBill.invoice_date || '' }] }))}
                className="text-[10px] bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 px-2 py-1 rounded">+ Add Line</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead><tr className="text-zinc-500 border-b border-zinc-800">
                  <th className="text-left px-2 py-1.5">Material</th>
                  <th className="text-right px-2 py-1.5">Qty</th>
                  <th className="text-right px-2 py-1.5">Rate</th>
                  <th className="text-right px-2 py-1.5">Subtotal</th>
                  <th className="text-right px-2 py-1.5">GST%</th>
                  <th className="text-right px-2 py-1.5">Total</th>
                  <th className="text-left px-2 py-1.5">Lot No</th>
                  <th className="text-left px-2 py-1.5">Recv. Date</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {editingBill.items.map((it, idx) => (
                    <EditBillRow key={`edit-row-${idx}`} item={it} idx={idx} setEditingBill={setEditingBill} materials={materials} canRemove={editingBill.items.length > 1} />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-amber-500/5">
                    <td colSpan={5} className="px-2 py-1.5 text-right text-zinc-400 font-semibold">Items Total:</td>
                    <td className="px-2 py-1.5 text-right text-emerald-300 font-mono font-bold text-sm">₹{itemsTotal.toFixed(2)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <ExtraChargesEditor
              charges={editingBill.extra_charges || []}
              onChange={(charges) => setEditingBill(b => ({ ...b, extra_charges: charges }))}
              itemsTotal={itemsTotal}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
            <button data-testid="edit-bill-save" type="submit" disabled={editBillSaving} className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50">{editBillSaving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditBillRow({ item: it, idx, setEditingBill, materials = [], canRemove }) {
  const recompute = (patch) => {
    const merged = { ...it, ...patch };
    const qty = parseFloat(merged.quantity) || 0;
    const rate = parseFloat(merged.unit_price) || 0;
    const gstRate = parseFloat(merged.gst_rate) || 0;
    const sub = qty * rate;
    const gst = sub * gstRate / 100;
    return { ...merged, subtotal: sub, gst_amount: gst, total_amount: sub + gst };
  };
  const apply = (patch) => {
    setEditingBill(b => ({ ...b, items: b.items.map((x, i) => i === idx ? recompute(patch) : x) }));
  };

  return (
    <tr className="border-b border-zinc-800/40">
      <td className="px-1 py-1 align-top">
        <RawMaterialNameAutocomplete
          value={it.product_name || ''}
          materials={materials}
          onChange={name => apply({ product_name: name })}
          onLastPrice={(hit) => {
            // Only auto-fill if the operator hasn't already typed a rate.
            const currentRate = parseFloat(it.unit_price) || 0;
            if (currentRate === 0 && hit?.last_rate > 0) {
              apply({ unit_price: hit.last_rate });
            }
          }}
          testId={`edit-bill-product-${idx}`}
        />
      </td>
      <td className="px-1 py-1">
        <input type="number" step="any" value={it.quantity || ''} onChange={e => apply({ quantity: e.target.value })}
          className="w-16 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-right text-zinc-100 focus:outline-none focus:border-amber-500"/>
      </td>
      <td className="px-1 py-1">
        <input type="number" step="any" value={it.unit_price || ''} onChange={e => apply({ unit_price: e.target.value })}
          className="w-20 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-right text-zinc-100 focus:outline-none focus:border-amber-500"/>
      </td>
      <td className="px-1 py-1 text-right text-zinc-400 font-mono">{(parseFloat(it.subtotal) || 0).toFixed(2)}</td>
      <td className="px-1 py-1">
        <input type="number" step="any" value={it.gst_rate || ''} onChange={e => apply({ gst_rate: e.target.value })}
          className="w-14 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-right text-zinc-100 focus:outline-none focus:border-amber-500"/>
      </td>
      <td className="px-1 py-1 text-right text-emerald-300 font-mono font-semibold">{(parseFloat(it.total_amount) || 0).toFixed(2)}</td>
      <td className="px-1 py-1">
        <input value={it.lot_no || ''} onChange={e => apply({ lot_no: e.target.value })} data-testid={`edit-bill-lot-no-${idx}`} placeholder="auto"
          className="w-24 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-zinc-100 focus:outline-none focus:border-amber-500"/>
      </td>
      <td className="px-1 py-1">
        <input type="date" value={(it.receiving_date || '').slice(0,10)} onChange={e => apply({ receiving_date: e.target.value })}
          className="w-32 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-1 text-zinc-100 focus:outline-none focus:border-amber-500"/>
      </td>
      <td className="px-1 py-1">
        {canRemove && (
          <button type="button"
            onClick={() => setEditingBill(b => ({ ...b, items: b.items.filter((_, i) => i !== idx) }))}
            className="p-1 rounded bg-red-900/30 text-red-400 hover:bg-red-900/50"><X size={10}/></button>
        )}
      </td>
    </tr>
  );
}
